import type React from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { sortActivities } from "routes/patients/utils/activityHelpers";

import { CalendarCard } from "../CalendarCard";
import type { ProtocolCard } from "../ProtocolTimeline/types";

interface ProtocolActivityGroupProps {
  activities: ProtocolCard[];
  isOverdue?: boolean;
}

export const ProtocolActivityGroup: React.FC<ProtocolActivityGroupProps> = ({ activities, isOverdue = true }) => {
  const { t } = useTranslation();

  return (
    <>
      {(!isOverdue && activities.length !== 0) ||
      (isOverdue &&
        activities.filter(activity => ("required" in activity ? activity.required : undefined)).length !== 0) ? (
        <Container data-testid="overdue" $isOverdue={isOverdue}>
          {isOverdue && <Title>{t("patients.protocol.overdue")}</Title>}
          <Activities>
            {isOverdue
              ? activities
                  .filter(activity => "required" in activity && activity.required)
                  .sort(sortActivities)
                  .map(activity => (
                    <CalendarCard
                      key={activity.id}
                      activity={activity}
                      smartReply={null}
                      activity_type={activity.activity_type_trimmed}
                      completed={activity.completed_on}
                      max_level={"max_level" in activity ? activity.max_level : undefined}
                      current_level={"current_level" in activity ? activity.current_level : undefined}
                      isPast={activity.overdue}
                    />
                  ))
              : activities
                  .sort(sortActivities)
                  .map(activity => (
                    <CalendarCard
                      key={activity.id}
                      activity={activity}
                      smartReply={null}
                      activity_type={activity.activity_type_trimmed}
                      completed={activity.completed_on}
                      max_level={"max_level" in activity ? activity.max_level : undefined}
                      current_level={"current_level" in activity ? activity.current_level : undefined}
                      isPast={activity.overdue}
                    />
                  ))}
          </Activities>
        </Container>
      ) : null}
    </>
  );
};

const Container = styled.div<{ $isOverdue: boolean }>`
  background-color: ${props => props.theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: ${({ $isOverdue }) => ($isOverdue ? "center" : "normal")};
  width: 100%;
  border-bottom: ${props => (props.$isOverdue ? `1px solid ${props.theme.colors.greys.silver}` : "none")};
  padding-bottom: ${props => props.theme.spacing.S_10};
`;

const Title = styled.div`
  margin: 0 ${props => props.theme.spacing.S_30};
  margin-top: ${props => props.theme.spacing.S_20};
  ${props => props.theme.font.header5}
  color: ${props => props.theme.colors.greys.dark};
`;

const Activities = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ theme }) => theme.aboveBreakpoint} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;
